/**
 * CaseTrackUS - Admin Dashboard
 * Full control panel for Panferov LLC administrators
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert,
  AppBar, Toolbar, Button, Card, CardContent,
  IconButton, Divider, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  Tooltip, Menu, MenuItem, Tab, Tabs
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://casetrackus.com';

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

export default function Admin({ user, onLogout }) {
  const token = localStorage.getItem('token');
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Manual status edit
  const [editCase, setEditCase] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, casesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users?token=${token}`),
        axios.get(`${API_URL}/admin/cases?token=${token}`)
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data.filter(u => !u.is_admin) : []);
      setCases(Array.isArray(casesRes.data) ? casesRes.data : []);
      setLastRefresh(new Date());
    } catch(e) {
      setError('Failed to load data: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      // System fields
      'Case ID', 'User Email', 'Account Type', 'Family Member ID',
      // Case identification
      'Case Type', 'Case #',
      // EOIR fields — raw values as stored in DB (from official EOIR source)
      'EOIR Alien Number', 'EOIR Alien Name',
      'EOIR Nationality Code', 'EOIR Nationality Label',
      'EOIR Docket Date',
      'EOIR Next Hearing Date', 'EOIR Hearing Time', 'EOIR Hearing Type', 'EOIR Hearing Type Code', 'EOIR Hearing Medium',
      'EOIR Judge Name',
      'EOIR Court Address', 'EOIR Base City Code',
      'EOIR Decision', 'EOIR Decision Date', 'EOIR Decision Code',
      'EOIR Clock Status', 'EOIR Elapsed Days',
      'EOIR Pending at BIA', 'EOIR BIA Decision', 'EOIR BIA Decision Date',
      'EOIR Appeal Filed', 'EOIR Appeal Filed Date', 'EOIR Appeal Due Date', 'EOIR Appeal Type',
      'EOIR Alien Brief Due', 'EOIR DHS Brief Due',
      'EOIR Case ID',
      // USCIS fields — from official USCIS source
      'USCIS Receipt Number', 'USCIS Form Type', 'USCIS Submitted Date',
      'USCIS Current Status', 'USCIS Last Check Details',
      // DOL fields — from official DOL FLAG source
      'DOL Case Number', 'DOL Case Type', 'DOL Case Status',
      'DOL Employer Name', 'DOL Job Title',
      'DOL Submitted Date',
      // Common
      'Last Check', 'Description'
    ];
    const rows = cases.map(c => {
      const owner = users.find(u => u.id === c.user_id);
      const lastCheck = c.checks && c.checks.length > 0 ? c.checks[0] : null;
      return [
        // System fields
        c.id || '',
        owner?.email || '',
        owner?.account_type || 'individual',
        c.family_member_id || '',
        // Case identification
        c.case_type?.toUpperCase() || '',
        c.case_type === 'uscis' ? (c.receipt_number || '') :
        c.case_type === 'dol' ? (c.dol_case_number || '') :
        (c.eoir_alien_number ? `A-${c.eoir_alien_number}` : ''),
        // EOIR — raw values preserved exactly as from EOIR official source
        c.eoir_alien_number || '',
        c.eoir_alien_name || '',
        c.eoir_nationality_code || '',
        c.eoir_nationality_label || '',
        c.eoir_docket_date || '',
        c.eoir_next_hearing_date || '',
        c.eoir_next_hearing_time || '',
        c.eoir_hearing_type === 'I' ? 'Individual Hearing' : c.eoir_hearing_type === 'M' ? 'Master Hearing' : (c.eoir_hearing_type || ''),
        c.eoir_hearing_type || '',
        c.eoir_hearing_medium || '',
        c.eoir_judge_name || '',
        c.eoir_court_address ? c.eoir_court_address.split('|').filter(Boolean).join(', ') : '',
        c.eoir_base_city_code || '',
        c.eoir_decision || '',
        c.eoir_decision_date || '',
        c.eoir_decision_code || '',
        c.eoir_clock_status === 'R' ? 'Running' : c.eoir_clock_status === 'X' ? 'Stopped' : (c.eoir_clock_status || ''),
        c.eoir_elapsed_days || '',
        c.eoir_pending_at_bia ? 'Yes' : '',
        c.eoir_bia_decision || '',
        c.eoir_bia_decision_date || '',
        c.eoir_appeal_filed ? 'Yes' : '',
        c.eoir_appeal_filed_date || '',
        c.eoir_appeal_due_date || '',
        c.eoir_appeal_type || '',
        c.eoir_alien_brief_due || '',
        c.eoir_dhs_brief_due || '',
        c.eoir_case_id || '',
        // USCIS — raw values from official USCIS source
        c.receipt_number || '',
        c.form_type || '',
        c.submitted_date || '',
        c.current_status || '',
        lastCheck?.details || '',
        // DOL — raw values from official DOL FLAG source
        c.dol_case_number || '',
        c.dol_case_type || '',
        c.dol_case_status || '',
        c.dol_employer_name || '',
        c.dol_job_title || '',
        c.dol_submitted_date || '',
        // Common
        c.checked_at || c.updated_at || '',
        c.description || ''
      ].map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `casetrackus_cases_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // Stats
  const totalUsers = users.length;
  const individualUsers = users.filter(u => u.account_type !== 'professional').length;
  const professionalUsers = users.filter(u => u.account_type === 'professional').length;
  const uscisCount = cases.filter(c => c.case_type === 'uscis').length;
  const eoirCount = cases.filter(c => c.case_type === 'eoir').length;
  const dolCount = cases.filter(c => c.case_type === 'dol').length;
  const pendingCount = cases.filter(c => c.status === 'pending').length;
  const failedCount = cases.filter(c => c.status === 'failed').length;
  const errorCount = cases.filter(c => c.status === 'error').length;

  const stats = [
    { label: 'Total Users', value: totalUsers, color: '#1565C0', sub: `${individualUsers} individual · ${professionalUsers} professional` },
    { label: 'USCIS Cases', value: uscisCount, color: '#2E7D32', sub: 'Receipt numbers' },
    { label: 'EOIR Cases', value: eoirCount, color: '#6A1B9A', sub: 'Court cases' },
    { label: 'DOL Cases', value: dolCount, color: '#E65100', sub: 'Labor certifications' },
    { label: 'Pending EOIR', value: pendingCount, color: '#F57C00', sub: 'In queue' },
    { label: 'Failed / Errors', value: failedCount + errorCount, color: '#C62828', sub: 'Need attention' },
  ];

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress sx={{ color: '#1B5E20' }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: '#F5F5F5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: '#1A237E' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>🗽 CaseTrackUS</Typography>
            <Chip label="Admin" size="small" sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastRefresh && (
              <Typography sx={{ fontSize: '0.72rem', opacity: 0.7 }}>
                Updated: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh data">
              <IconButton color="inherit" onClick={loadData} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Daily Report">
              <IconButton color="inherit" onClick={async () => {
                setReportLoading(true);
                setReportOpen(true);
                try {
                  const res = await axios.get(`${API_URL}/admin/daily-report?token=${token}`);
                  setReportData(res.data);
                } catch(e) {
                  setReportData({ error: e.response?.data?.detail || e.message });
                } finally {
                  setReportLoading(false);
                }
              }} size="small">
                <AssessmentIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export all cases to CSV">
              <IconButton color="inherit" onClick={handleExportCSV} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Typography sx={{ fontSize: '0.85rem', opacity: 0.8, ml: 1 }}>{user?.email}</Typography>
            <IconButton color="inherit" onClick={onLogout} size="small">
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, mb: 3 }}>
          {stats.map(s => (
            <Card key={s.label} sx={{ borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
              <CardContent sx={{ py: 1.5, px: 1 }}>
                <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: s.color }}>{s.value}</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#333' }}>{s.label}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>{s.sub}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Failed/Error cases alert */}
        {(failedCount + errorCount) > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
            <strong>{failedCount + errorCount} cases need attention</strong> — failed checks or errors detected.
            Review cases marked with ⚠️ below.
          </Alert>
        )}

        {/* Users and their cases */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {users.map(u => (
            <UserRow key={u.id} user={u}
              cases={cases.filter(c => c.user_id === u.id)}
              token={token}
              onEdit={(c) => { setEditCase(c); setEditStatus(c.eoir_decision || c.status_checks?.[0]?.status || ''); }}
              onRefresh={loadData} />
          ))}
          {users.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6, background: 'white', borderRadius: 2 }}>
              <Typography sx={{ color: '#999' }}>No users registered yet</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Daily Report Dialog */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          📊 Daily Report — {reportData?.date || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </DialogTitle>
        <DialogContent dividers>
          {reportLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
          {reportData?.error && <Alert severity="error">{reportData.error}</Alert>}
          {reportData && !reportData.error && !reportLoading && (
            <Box>
              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center', p: 2, background: '#F5F5F5', borderRadius: 2, minWidth: 100 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#1565C0' }}>{reportData.total_checked}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>Total Checked</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 2, background: '#E8F5E9', borderRadius: 2, minWidth: 100 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#2E7D32' }}>{reportData.total_changed}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>Status Changed</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 2, background: '#F5F5F5', borderRadius: 2, minWidth: 100 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#888' }}>{reportData.total_no_change}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>No Change</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 2, background: '#FFEBEE', borderRadius: 2, minWidth: 100 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#C62828' }}>{reportData.total_errors}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>Errors</Typography>
                </Box>
              </Box>

              {/* Changed cases */}
              {reportData.changed?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#2E7D32', mb: 1 }}>
                    ✅ Status Changed ({reportData.changed.length})
                  </Typography>
                  {reportData.changed.map((c, i) => (
                    <Box key={i} sx={{ p: 1.5, mb: 0.5, background: '#E8F5E9', borderRadius: 1, border: '1px solid #C8E6C9' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={c.case_type.toUpperCase()} size="small"
                          sx={{ fontSize: '0.65rem', fontWeight: 700,
                            background: c.case_type === 'eoir' ? '#E8D5F5' : '#DCEEFA',
                            color: c.case_type === 'eoir' ? '#6A1B9A' : '#0D47A1' }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.case_number}</Typography>
                        {c.alien_name && <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>{c.alien_name}</Typography>}
                        <Typography sx={{ fontSize: '0.75rem', color: '#555', flex: 1 }}>{c.user_email}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
                          {c.checked_at ? new Date(c.checked_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </Typography>
                      </Box>
                      {c.status && <Typography sx={{ fontSize: '0.78rem', color: '#2E7D32', mt: 0.5 }}>{c.status}</Typography>}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Errors */}
              {reportData.errors?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#C62828', mb: 1 }}>
                    ❌ Errors ({reportData.errors.length})
                  </Typography>
                  {reportData.errors.map((c, i) => (
                    <Box key={i} sx={{ p: 1.5, mb: 0.5, background: '#FFEBEE', borderRadius: 1, border: '1px solid #FFCDD2' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={c.case_type.toUpperCase()} size="small" sx={{ fontSize: '0.65rem', fontWeight: 700, background: '#DCEEFA', color: '#0D47A1' }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.case_number}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#555', flex: 1 }}>{c.user_email}</Typography>
                      </Box>
                      {c.status && <Typography sx={{ fontSize: '0.78rem', color: '#C62828', mt: 0.5 }}>{c.status}</Typography>}
                    </Box>
                  ))}
                </Box>
              )}

              {/* No change */}
              {reportData.no_change?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#888', mb: 1 }}>
                    — No Change ({reportData.no_change.length})
                  </Typography>
                  {reportData.no_change.map((c, i) => (
                    <Box key={i} sx={{ p: 1, mb: 0.3, background: '#FAFAFA', borderRadius: 1, border: '1px solid #EEE',
                      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={c.case_type.toUpperCase()} size="small"
                        sx={{ fontSize: '0.65rem', fontWeight: 700,
                          background: c.case_type === 'eoir' ? '#E8D5F5' : '#DCEEFA',
                          color: c.case_type === 'eoir' ? '#6A1B9A' : '#0D47A1' }} />
                      <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{c.case_number}</Typography>
                      {c.alien_name && <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{c.alien_name}</Typography>}
                      <Typography sx={{ fontSize: '0.72rem', color: '#aaa', flex: 1 }}>{c.user_email}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#bbb' }}>
                        {c.checked_at ? new Date(c.checked_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {reportData.total_checked === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No checks performed today yet.</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Case Edit Dialog */}
      <AdminEditDialog
        editCase={editCase}
        token={token}
        apiUrl={API_URL}
        onClose={() => setEditCase(null)}
        onSaved={() => { setEditCase(null); loadData(); }}
      />
    </Box>
  );
}


// ─────────────────────────────────────
// User Row with expandable cases
// ─────────────────────────────────────
function UserRow({ user, cases, token, onEdit, onRefresh }) {
  const [expanded, setExpanded] = useState(false);

  const hasProblems = cases.some(c => c.status === 'failed' || c.status === 'error');
  const uscis = cases.filter(c => c.case_type === 'uscis');
  const eoir = cases.filter(c => c.case_type === 'eoir');
  const dol = cases.filter(c => c.case_type === 'dol');

  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      borderLeft: hasProblems ? '4px solid #C62828' : '4px solid transparent' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '1.2rem' }}>
            {user.account_type === 'professional' ? '🏢' : '👤'}
          </Typography>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{user.full_name || user.email}</Typography>
              {hasProblems && <Chip label="⚠️ Issues" size="small" color="error" sx={{ fontSize: '0.65rem', height: 18 }} />}
              <Chip label={user.account_type === 'professional' ? 'Professional' : 'Individual'}
                size="small" sx={{ fontSize: '0.65rem', height: 18,
                  background: user.account_type === 'professional' ? '#E3F2FD' : '#F3E5F5',
                  color: user.account_type === 'professional' ? '#1565C0' : '#6A1B9A' }} />
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>{user.email}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
              {uscis.length > 0 && <Chip label={`${uscis.length} USCIS`} size="small" sx={{ fontSize: '0.65rem', height: 16, background: '#DCEEFA', color: '#0D47A1' }} />}
              {eoir.length > 0 && <Chip label={`${eoir.length} EOIR`} size="small" sx={{ fontSize: '0.65rem', height: 16, background: '#E8D5F5', color: '#6A1B9A' }} />}
              {dol.length > 0 && <Chip label={`${dol.length} DOL`} size="small" sx={{ fontSize: '0.65rem', height: 16, background: '#FFF3E0', color: '#E65100' }} />}
              {cases.length === 0 && <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>No cases</Typography>}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#999' }}>
            Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </Typography>
          <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>
      </Box>

      {expanded && cases.length > 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {cases.map(c => <AdminCaseRow key={c.id} c={c} onEdit={() => onEdit(c)} token={token} apiUrl={API_URL} onRefresh={onRefresh} />)}
          </Box>
        </Box>
      )}

      {expanded && cases.length === 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography sx={{ color: '#bbb', fontSize: '0.85rem', textAlign: 'center', py: 1 }}>
            This user has no cases
          </Typography>
        </Box>
      )}
    </Card>
  );
}


// ─────────────────────────────────────
// Admin Case Row
// ─────────────────────────────────────
function AdminCaseRow({ c, onEdit, token, apiUrl, onRefresh }) {
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  const handleHistory = async () => {
    try {
      const res = await axios.get(`${apiUrl}/cases/${c.id}/history?token=${token}`);
      const data = res.data;
      const checks = data.checks || data || [];
      setHistoryData(Array.isArray(checks) ? checks : []);
      setHistoryOpen(true);
    } catch(e) {
      setHistoryData([]);
      setHistoryOpen(true);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    setCheckMsg('');
    try {
      if (c.case_type === 'eoir') {
        await axios.post(`${apiUrl}/eoir-queue/add?case_id=${c.id}&token=${token}`);
        setCheckMsg('✅ Added to EOIR queue');
      } else if (c.case_type === 'uscis') {
        await axios.post(`${apiUrl}/cases/${c.id}/check?token=${token}`);
        setCheckMsg('✅ Status updated');
        if (onRefresh) onRefresh();
      } else if (c.case_type === 'dol') {
        await axios.post(`${apiUrl}/cases/${c.id}/check-dol?token=${token}`);
        setCheckMsg('✅ DOL status updated');
        if (onRefresh) onRefresh();
      }
    } catch(e) {
      setCheckMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally {
      setChecking(false);
      setTimeout(() => setCheckMsg(''), 4000);
    }
  };

  const typeColors = {
    eoir: { bg: '#E8D5F5', color: '#6A1B9A' },
    uscis: { bg: '#DCEEFA', color: '#0D47A1' },
    dol: { bg: '#FFF3E0', color: '#E65100' }
  };
  const colors = typeColors[c.case_type] || typeColors.uscis;

  const isFailed = c.status === 'failed' || c.status === 'error';
  const isPending = c.status === 'pending' || c.status === 'processing';

  const caseNum = c.case_type === 'uscis' ? c.receipt_number :
                  c.case_type === 'dol' ? c.dol_case_number :
                  `A-${c.eoir_alien_number}`;

  // Fix: use checked_at for USCIS/EOIR, updated_at for DOL
  const rawDate = c.case_type === 'dol'
    ? (c.updated_at && c.updated_at !== 'None' ? c.updated_at : null)
    : (c.checked_at && c.checked_at !== 'None' && c.checked_at !== 'null' ? c.checked_at : null);
  const checkedAt = rawDate ? new Date(rawDate) : null;
  const checkedAtValid = checkedAt && !isNaN(checkedAt);

  // Status text per case type
  const statusText = c.case_type === 'eoir'
    ? (c.eoir_pending_at_bia
        ? (c.eoir_decision ? c.eoir_decision + ' — Pending at BIA' : 'Pending at BIA')
        : c.eoir_decision || (c.eoir_next_hearing_date ? 'Hearing scheduled' : 'Pending'))
    : c.case_type === 'uscis'
    ? (c.current_status || 'Not checked yet')
    : (c.dol_case_status || 'Not checked yet');

  return (
    <>
    <Box sx={{ p: 1.5, background: isFailed ? '#FFF5F5' : isPending ? '#FFFDE7' : '#FAFAFA',
      borderRadius: 1, border: `1px solid ${isFailed ? '#FFCDD2' : isPending ? '#FFF9C4' : '#EEE'}`,
      mb: 0.5 }}>

      {/* Top row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip label={c.case_type.toUpperCase()} size="small"
          sx={{ fontSize: '0.65rem', fontWeight: 700, background: colors.bg, color: colors.color }} />

        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{caseNum}</Typography>

        {c.eoir_alien_name && (
          <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>{c.eoir_alien_name}</Typography>
        )}
        {c.dol_employer_name && (
          <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>🏢 {c.dol_employer_name}</Typography>
        )}
        {c.dol_job_title && (
          <Typography sx={{ fontSize: '0.78rem', color: '#777' }}>💼 {c.dol_job_title}</Typography>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Last checked date */}
        {checkedAtValid && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>Last check</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
              {checkedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>
        )}

        {/* Next hearing */}
        {c.eoir_next_hearing_date && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#888' }}>Next hearing</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#C62828' }}>
              {new Date(c.eoir_next_hearing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>
        )}

        <Tooltip title="Check Status Now">
          <IconButton size="small" onClick={handleCheck} disabled={checking}
            sx={{ color: '#1565C0' }}>
            {checking ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="View History">
          <IconButton size="small" onClick={handleHistory}
            sx={{ color: '#388E3C' }}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="View / Edit case">
          <IconButton size="small" onClick={onEdit} sx={{ color: '#1B5E20' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status row */}
      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {isFailed ? (
          <Chip label="⚠️ Failed" size="small" color="error" sx={{ fontSize: '0.65rem', height: 18 }} />
        ) : isPending ? (
          <Chip label="⏳ Pending" size="small" sx={{ fontSize: '0.65rem', height: 18, background: '#FFF9C4', color: '#F57C00' }} />
        ) : (
          <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>{statusText}</Typography>
        )}
        {c.eoir_pending_at_bia && !isFailed && !isPending && (
          <Chip label="Pending at BIA" size="small"
            sx={{ fontSize: '0.65rem', height: 18, background: '#FFF3E0', color: '#E65100' }} />
        )}
        {c.eoir_appeal_due_date && (
          <Chip label={`⚠️ Appeal due: ${c.eoir_appeal_due_date}`} size="small" color="error"
            sx={{ fontSize: '0.65rem', height: 18 }} />
        )}
        {c.eoir_clock_status && (
          <Typography sx={{ fontSize: '0.72rem', color: c.eoir_clock_status === 'X' ? '#C62828' : '#2E7D32' }}>
            Clock: {c.eoir_clock_status === 'X' ? '⏹ Stopped' : '▶ Running'}
            {c.eoir_elapsed_days && c.eoir_elapsed_days !== '0' ? ` (${c.eoir_elapsed_days} days)` : ''}
          </Typography>
        )}
        {checkMsg && (
          <Typography sx={{ fontSize: '0.72rem', color: checkMsg.startsWith('✅') ? '#2E7D32' : '#C62828' }}>
            {checkMsg}
          </Typography>
        )}
      </Box>
    </Box>
      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          📋 History — {c.case_type === 'uscis' ? c.receipt_number : c.case_type === 'dol' ? c.dol_case_number : `A-${c.eoir_alien_number}`}
        </DialogTitle>
        <DialogContent dividers>
          {historyData.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No history yet</Typography>
          ) : (
            historyData.map((h, i) => (
              <Box key={i} sx={{ mb: 2, pb: 2, borderBottom: i < historyData.length - 1 ? '1px solid #EEE' : 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Chip label={h.status || h.status_summary || 'Check'} size="small" color="default" />
                  <Typography variant="caption" color="text.secondary">
                    {h.checked_at ? new Date(h.checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </Typography>
                </Box>
                {h.details && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                    {h.details}
                  </Typography>
                )}
                {h.status_changed && (
                  <Chip label="Changed" size="small" color="success" sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />
                )}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


// ─────────────────────────────────────
// Admin Edit Dialog — View + Edit tabs
// ─────────────────────────────────────
function AdminEditDialog({ editCase, token, apiUrl, onClose, onSaved }) {
  const [tab, setTab] = useState(0);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    if (editCase) {
      setFields({
        // USCIS
        receipt_number: editCase.receipt_number || '',
        form_type: editCase.form_type || '',
        submitted_date: editCase.submitted_date || '',
        current_status: editCase.current_status || '',
        description: editCase.description || '',
        // EOIR
        eoir_alien_number: editCase.eoir_alien_number || '',
        eoir_alien_name: editCase.eoir_alien_name || '',
        eoir_nationality_code: editCase.eoir_nationality_code || '',
        eoir_nationality_label: editCase.eoir_nationality_label || '',
        eoir_docket_date: editCase.eoir_docket_date || '',
        eoir_judge_name: editCase.eoir_judge_name || '',
        eoir_court_address: editCase.eoir_court_address || '',
        eoir_base_city_code: editCase.eoir_base_city_code || '',
        eoir_next_hearing_date: editCase.eoir_next_hearing_date || '',
        eoir_next_hearing_time: editCase.eoir_next_hearing_time || '',
        eoir_hearing_type: editCase.eoir_hearing_type || '',
        eoir_hearing_medium: editCase.eoir_hearing_medium || '',
        eoir_decision: editCase.eoir_decision || '',
        eoir_decision_date: editCase.eoir_decision_date || '',
        eoir_decision_code: editCase.eoir_decision_code || '',
        eoir_clock_status: editCase.eoir_clock_status || '',
        eoir_elapsed_days: editCase.eoir_elapsed_days || '',
        eoir_pending_at_bia: editCase.eoir_pending_at_bia || false,
        eoir_bia_decision: editCase.eoir_bia_decision || '',
        eoir_bia_decision_date: editCase.eoir_bia_decision_date || '',
        eoir_appeal_filed: editCase.eoir_appeal_filed || false,
        eoir_appeal_filed_date: editCase.eoir_appeal_filed_date || '',
        eoir_appeal_due_date: editCase.eoir_appeal_due_date || '',
        eoir_appeal_type: editCase.eoir_appeal_type || '',
        eoir_alien_brief_due: editCase.eoir_alien_brief_due || '',
        eoir_dhs_brief_due: editCase.eoir_dhs_brief_due || '',
        // DOL
        dol_case_number: editCase.dol_case_number || '',
        dol_case_type: editCase.dol_case_type || '',
        dol_case_status: editCase.dol_case_status || '',
        dol_employer_name: editCase.dol_employer_name || '',
        dol_job_title: editCase.dol_job_title || '',
        dol_submitted_date: editCase.dol_submitted_date || '',
      });
      setTab(0);
      setSaveMsg('');
    }
  }, [editCase]);

  const f = (key) => fields[key] ?? '';
  const set = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await axios.put(`${apiUrl}/admin/cases/${editCase.id}?token=${token}`, fields);
      setSaveMsg('✅ Saved successfully');
      setTimeout(() => onSaved(), 1000);
    } catch(e) {
      setSaveMsg('❌ Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!editCase) return null;

  const caseNum = editCase.case_type === 'uscis' ? editCase.receipt_number :
                  editCase.case_type === 'dol' ? editCase.dol_case_number :
                  `A-${editCase.eoir_alien_number}`;

  const typeColors = {
    eoir: { bg: '#E8D5F5', color: '#6A1B9A' },
    uscis: { bg: '#DCEEFA', color: '#0D47A1' },
    dol: { bg: '#FFF3E0', color: '#E65100' }
  };
  const colors = typeColors[editCase.case_type] || typeColors.uscis;

  const decisionColor = editCase.eoir_decision_code === 'R' ? '#2E7D32'
    : editCase.eoir_decision_code === 'D' ? '#C62828'
    : editCase.eoir_decision_code === 'V' ? '#1565C0' : '#E65100';

  return (
    <Dialog open={!!editCase} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, pb: 0 }}>
        <Chip label={editCase.case_type?.toUpperCase()} size="small"
          sx={{ background: colors.bg, color: colors.color, fontWeight: 700 }} />
        {caseNum}
        {editCase.eoir_alien_name && (
          <Typography sx={{ fontSize: '0.85rem', color: '#666', ml: 1 }}>{editCase.eoir_alien_name}</Typography>
        )}
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid #EEE' }}>
        <Tab label="👁 View (as user sees it)" />
        <Tab label="✏️ Edit (manual override)" />
      </Tabs>

      <DialogContent sx={{ pt: 2 }}>

        {/* ── TAB 0: VIEW ── */}
        {tab === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
              This is how the user sees this case. To correct data, switch to the Edit tab.
            </Alert>

            {/* USCIS View */}
            {editCase.case_type === 'uscis' && (
              <Box>
                {editCase.form_type && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Form: {editCase.form_type}{editCase.submitted_date && ` • Submitted: ${editCase.submitted_date}`}
                  </Typography>
                )}
                {editCase.current_status ? (
                  <Chip label={editCase.current_status} color="success" size="small" sx={{ mb: 1 }} />
                ) : (
                  <Chip label="Not checked yet" color="default" size="small" sx={{ mb: 1 }} />
                )}
                {editCase.current_status_details && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                    {editCase.current_status_details}
                  </Typography>
                )}
              </Box>
            )}

            {/* EOIR View */}
            {editCase.case_type === 'eoir' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Nationality: {editCase.eoir_nationality_label || editCase.eoir_nationality_code}
                  {editCase.eoir_docket_date && ` • Docket: ${fmtDate(editCase.eoir_docket_date)}`}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box sx={{ background: '#F8F9FA', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>📅 Next Hearing</Typography>
                    {editCase.eoir_next_hearing_date ? (
                      <>
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                          {fmtDate(editCase.eoir_next_hearing_date)}{editCase.eoir_next_hearing_time && ` at ${editCase.eoir_next_hearing_time}`}
                        </Typography>
                        {editCase.eoir_hearing_type && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {editCase.eoir_hearing_type === 'I' ? 'Individual Hearing' : editCase.eoir_hearing_type === 'M' ? 'Master Hearing' : editCase.eoir_hearing_type}
                          </Typography>
                        )}
                        {editCase.eoir_judge_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Judge: {editCase.eoir_judge_name}</Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>No future hearings scheduled</Typography>
                    )}
                  </Box>
                  <Box sx={{ background: '#F8F9FA', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>⚖️ Court Decision</Typography>
                    {editCase.eoir_decision ? (
                      <>
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5, color: decisionColor }}>{editCase.eoir_decision}</Typography>
                        {editCase.eoir_decision_date && (
                          <Typography variant="caption" color="text.secondary">Date: {fmtDate(editCase.eoir_decision_date)}</Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>No decision yet</Typography>
                    )}
                    {editCase.eoir_court_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        📍 {editCase.eoir_court_address.split('|').filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                  {(editCase.eoir_appeal_filed || editCase.eoir_pending_at_bia) && (
                    <Box sx={{ background: '#FFF8E1', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>📋 BIA Appeal</Typography>
                      {editCase.eoir_appeal_due_date && (
                        <Box sx={{ mt: 0.5, p: 1, background: '#FFEBEE', borderRadius: 1 }}>
                          <Typography variant="body2" color="error" fontWeight="bold">⚠️ Deadline: {fmtDate(editCase.eoir_appeal_due_date)}</Typography>
                        </Box>
                      )}
                      {editCase.eoir_pending_at_bia && <Chip label="Pending at BIA" color="warning" size="small" sx={{ mt: 0.5 }} />}
                      {editCase.eoir_bia_decision && (
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>BIA: {editCase.eoir_bia_decision}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Alien brief: {editCase.eoir_alien_brief_due ? `due ${fmtDate(editCase.eoir_alien_brief_due)}` : 'No due date at this time'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        DHS brief: {editCase.eoir_dhs_brief_due ? `due ${fmtDate(editCase.eoir_dhs_brief_due)}` : 'No due date at this time'}
                      </Typography>
                    </Box>
                  )}
                  {editCase.eoir_clock_status && (
                    <Box sx={{ background: '#F3F4F6', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>⏱ Asylum Clock</Typography>
                      <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5,
                        color: editCase.eoir_clock_status === 'X' ? '#C62828' : '#2E7D32' }}>
                        {editCase.eoir_clock_status === 'X' ? 'Stopped' : 'Running'}
                      </Typography>
                      {editCase.eoir_elapsed_days && (
                        <Typography variant="caption" color="text.secondary">{editCase.eoir_elapsed_days} days elapsed</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* DOL View */}
            {editCase.case_type === 'dol' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {editCase.dol_case_type && (
                    <Chip label={editCase.dol_case_type} size="small"
                      sx={{ background: '#F3E5F5', color: '#6A1B9A', fontWeight: 700 }} />
                  )}
                  <Typography variant="body2" color="text.secondary">{editCase.dol_case_number}</Typography>
                </Box>
                {editCase.dol_employer_name && (
                  <Typography variant="body1" fontWeight="500" sx={{ mb: 0.5 }}>🏢 {editCase.dol_employer_name}</Typography>
                )}
                {editCase.dol_job_title && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>💼 {editCase.dol_job_title}</Typography>
                )}
                {editCase.dol_case_status && (
                  <Chip label={editCase.dol_case_status} size="small" sx={{ mb: 1,
                    background: editCase.dol_case_status.includes('CERTIFIED') && !editCase.dol_case_status.includes('EXPIRED') ? '#E8F5E9' : '#FFF3E0',
                    color: editCase.dol_case_status.includes('CERTIFIED') && !editCase.dol_case_status.includes('EXPIRED') ? '#2E7D32' : '#E65100',
                    fontWeight: 700 }} />
                )}
                {editCase.dol_submitted_date && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Submitted: {editCase.dol_submitted_date}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── TAB 1: EDIT ── */}
        {tab === 1 && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
              Manual override. All data is sourced from official USCIS / EOIR / DOL sources. Edit only if automatic checks have failed.
            </Alert>

            {/* USCIS Edit */}
            {editCase.case_type === 'uscis' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.72rem' }}>USCIS — Official USCIS Source</Typography>
                <TextField size="small" fullWidth label="Receipt Number" value={f('receipt_number')} onChange={e => set('receipt_number', e.target.value)} />
                <TextField size="small" fullWidth label="Form Type (e.g. I-130)" value={f('form_type')} onChange={e => set('form_type', e.target.value)} />
                <TextField size="small" fullWidth label="Submitted Date" value={f('submitted_date')} onChange={e => set('submitted_date', e.target.value)} />
                <TextField size="small" fullWidth label="Current Status" value={f('current_status')} onChange={e => set('current_status', e.target.value)} />
                <TextField size="small" fullWidth multiline rows={2} label="Description" value={f('description')} onChange={e => set('description', e.target.value)} />
              </Box>
            )}

            {/* EOIR Edit */}
            {editCase.case_type === 'eoir' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.72rem' }}>EOIR — Official EOIR Source</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Alien Number (без A-)" value={f('eoir_alien_number')} onChange={e => set('eoir_alien_number', e.target.value)} />
                  <TextField size="small" fullWidth label="Alien Name (LAST, FIRST)" value={f('eoir_alien_name')} onChange={e => set('eoir_alien_name', e.target.value)} />
                  <TextField size="small" fullWidth label="Nationality Code" value={f('eoir_nationality_code')} onChange={e => set('eoir_nationality_code', e.target.value)} />
                  <TextField size="small" fullWidth label="Nationality Label" value={f('eoir_nationality_label')} onChange={e => set('eoir_nationality_label', e.target.value)} />
                  <TextField size="small" fullWidth label="Docket Date" value={f('eoir_docket_date')} onChange={e => set('eoir_docket_date', e.target.value)} />
                  <TextField size="small" fullWidth label="Judge Name" value={f('eoir_judge_name')} onChange={e => set('eoir_judge_name', e.target.value)} />
                  <TextField size="small" fullWidth label="Base City Code (e.g. CHI)" value={f('eoir_base_city_code')} onChange={e => set('eoir_base_city_code', e.target.value)} />
                  <TextField size="small" fullWidth label="Court Address (через |)" value={f('eoir_court_address')} onChange={e => set('eoir_court_address', e.target.value)} />
                </Box>
                <Divider><Typography variant="caption">Hearing</Typography></Divider>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Next Hearing Date" value={f('eoir_next_hearing_date')} onChange={e => set('eoir_next_hearing_date', e.target.value)} />
                  <TextField size="small" fullWidth label="Hearing Time" value={f('eoir_next_hearing_time')} onChange={e => set('eoir_next_hearing_time', e.target.value)} />
                  <TextField size="small" fullWidth label="Hearing Type (I/M)" value={f('eoir_hearing_type')} onChange={e => set('eoir_hearing_type', e.target.value)} />
                  <TextField size="small" fullWidth label="Hearing Medium" value={f('eoir_hearing_medium')} onChange={e => set('eoir_hearing_medium', e.target.value)} />
                </Box>
                <Divider><Typography variant="caption">Decision</Typography></Divider>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Decision" value={f('eoir_decision')} onChange={e => set('eoir_decision', e.target.value)} />
                  <TextField size="small" fullWidth label="Decision Date" value={f('eoir_decision_date')} onChange={e => set('eoir_decision_date', e.target.value)} />
                  <TextField size="small" fullWidth label="Decision Code (R/D/V)" value={f('eoir_decision_code')} onChange={e => set('eoir_decision_code', e.target.value)} />
                </Box>
                <Divider><Typography variant="caption">Asylum Clock</Typography></Divider>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Clock Status (R=Running / X=Stopped)" value={f('eoir_clock_status')} onChange={e => set('eoir_clock_status', e.target.value)} />
                  <TextField size="small" fullWidth label="Elapsed Days" value={f('eoir_elapsed_days')} onChange={e => set('eoir_elapsed_days', e.target.value)} />
                </Box>
                <Divider><Typography variant="caption">BIA Appeal</Typography></Divider>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Appeal Filed Date" value={f('eoir_appeal_filed_date')} onChange={e => set('eoir_appeal_filed_date', e.target.value)} />
                  <TextField size="small" fullWidth label="Appeal Due Date" value={f('eoir_appeal_due_date')} onChange={e => set('eoir_appeal_due_date', e.target.value)} />
                  <TextField size="small" fullWidth label="Appeal Type" value={f('eoir_appeal_type')} onChange={e => set('eoir_appeal_type', e.target.value)} />
                  <TextField size="small" fullWidth label="BIA Decision" value={f('eoir_bia_decision')} onChange={e => set('eoir_bia_decision', e.target.value)} />
                  <TextField size="small" fullWidth label="BIA Decision Date" value={f('eoir_bia_decision_date')} onChange={e => set('eoir_bia_decision_date', e.target.value)} />
                  <TextField size="small" fullWidth label="Alien Brief Due" value={f('eoir_alien_brief_due')} onChange={e => set('eoir_alien_brief_due', e.target.value)} />
                  <TextField size="small" fullWidth label="DHS Brief Due" value={f('eoir_dhs_brief_due')} onChange={e => set('eoir_dhs_brief_due', e.target.value)} />
                </Box>
              </Box>
            )}

            {/* DOL Edit */}
            {editCase.case_type === 'dol' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.72rem' }}>DOL — Official DOL FLAG Source</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Case Number" value={f('dol_case_number')} onChange={e => set('dol_case_number', e.target.value)} />
                  <TextField size="small" fullWidth label="Case Type (PERM/LCA)" value={f('dol_case_type')} onChange={e => set('dol_case_type', e.target.value)} />
                  <TextField size="small" fullWidth label="Case Status" value={f('dol_case_status')} onChange={e => set('dol_case_status', e.target.value)} />
                  <TextField size="small" fullWidth label="Employer Name" value={f('dol_employer_name')} onChange={e => set('dol_employer_name', e.target.value)} />
                  <TextField size="small" fullWidth label="Job Title" value={f('dol_job_title')} onChange={e => set('dol_job_title', e.target.value)} />
                  <TextField size="small" fullWidth label="Submitted Date" value={f('dol_submitted_date')} onChange={e => set('dol_submitted_date', e.target.value)} />
                </Box>
              </Box>
            )}

            {saveMsg && (
              <Alert severity={saveMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mt: 2 }}>
                {saveMsg}
              </Alert>
            )}
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tab === 1 && (
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ background: '#1B5E20' }}>
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
